/**
 * Tedarik Kategorileri — satın alma gruplaması (Bar, Kasap, Hal, Temizlik...).
 *
 * Bu kategoriler "hangi malı kimden alıyoruz" ayrımının temeli: kaleme
 * (Ingredient/SupplyItem) ve tedarikçiye (M2M) atanır, RFQ'da "Kategoriden Ekle"
 * ve tedarikçi önerisi bunları kullanır.
 *
 * Kategori = SADECE satın alma grubu — kaleme tip dayatmaz; bir kategoride hem
 * mutfak hammaddesi hem mutfak-dışı kalem olabilir.
 *
 * Silme = soft-delete (backend is_active=False) — geçmiş bağlar korunur.
 */
import {
  ActionIcon, Badge, Box, Button, Card, Center, Drawer, Group, Loader, Paper,
  SegmentedControl, Select, Stack, Table, Text, TextInput, ThemeIcon,
} from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import {
  IconBuildingStore, IconCheck, IconCheese, IconEdit, IconGripVertical, IconPlus,
  IconSearch, IconSettings, IconShoppingCart, IconSparkles, IconTags, IconTrash,
  IconTruck, IconX,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import {
  useResource, useRemove, useUpdate, useCreate, useInfiniteResource,
  useCategoryItems, useReorderProcurementCategories,
} from "../../../shared/api";
import {
  EmptyState, PageHeader, ResourceForm, confirmDelete, useInfiniteScrollRef,
  type FormField,
} from "../../../shared/ui";
import { SupplierPickerModal } from "../components/SupplierPickerModal";

const FIELDS: FormField[] = [
  {
    name: "code",
    label: "Kod (stabil anahtar — BAR, KASAP, TEMIZLIK...)",
    required: true,
  },
  { name: "name", label: "Ad", required: true },
  {
    name: "sort_order",
    label: "Sıra",
    type: "select",
    defaultValue: "0",
    options: Array.from({ length: 21 }, (_, i) => ({
      value: String(i),
      label: String(i),
    })),
  },
  { name: "is_active", label: "Aktif", type: "switch", defaultValue: true },
];

export function ProcurementCategoriesPage() {
  const { data, isLoading } = useResource<any>("procurement-categories");
  const remove = useRemove("procurement-categories");
  const reorder = useReorderProcurementCategories();
  const [opened, { open, close }] = useDisclosure(false);
  const [editing, setEditing] = useState<any | null>(null);
  // Detay drawer: kategoriye tıkla → kalemler + tedarikçiler tek ekranda.
  const [detail, setDetail] = useState<any | null>(null);
  // Sürükle-bırak sıralama: backend'den gelen liste sort_order'a göre.
  const [dragId, setDragId] = useState<string | null>(null);
  const serverList: any[] = (data?.results || data || [])
    .slice()
    .sort((a: any, b: any) => a.sort_order - b.sort_order || a.name.localeCompare(b.name));
  // Lokal sıralı kopya (drag sırasında anında güncellenir).
  const [rows, setRows] = useState<any[]>([]);
  useEffect(() => { setRows(serverList); /* eslint-disable-next-line */ }, [data]);
  const list = rows;

  function handleDrop(targetId: string) {
    if (!dragId || dragId === targetId) { setDragId(null); return; }
    const from = rows.findIndex((r) => r.id === dragId);
    const to = rows.findIndex((r) => r.id === targetId);
    if (from < 0 || to < 0) { setDragId(null); return; }
    const next = [...rows];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setRows(next);
    setDragId(null);
    reorder.mutate(next.map((r) => r.id), {
      onSuccess: () => notifications.show({ message: "Sıralama kaydedildi", color: "green" }),
      onError: () => {
        notifications.show({ message: "Sıralama kaydedilemedi", color: "red" });
        setRows(serverList);
      },
    });
  }

  if (isLoading) return <Center h={300}><Loader /></Center>;

  function startCreate() {
    setEditing(null);
    open();
  }
  function startEdit(c: any) {
    setEditing(c);
    open();
  }
  async function handleDelete(c: any) {
    const ok = await confirmDelete(
      `"${c.name}" kategorisi pasifleştirilsin mi? (Geçmiş kayıtlar korunur, listelerde görünmez.)`,
    );
    if (!ok) return;
    await remove.mutateAsync(c.id);
  }

  return (
    <Stack gap="md">
      <PageHeader
        title="Tedarik Kategorileri"
        subtitle="Satın alma grupları — Bar, Kasap, Hal, Temizlik... (kimden alınır)"
        actions={
          <Button leftSection={<IconPlus size={16} />} onClick={startCreate}>
            Yeni Kategori
          </Button>
        }
      />

      {list.length === 0 ? (
        <EmptyState
          title="Henüz tedarik kategorisi yok"
          description="Bar, Kasap, Hal gibi satın alma gruplarını ekleyin."
        />
      ) : (
        <Card withBorder p={0}>
          <Table.ScrollContainer minWidth={500}>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={36}></Table.Th>
                  <Table.Th>Kod</Table.Th>
                  <Table.Th>Ad</Table.Th>
                  <Table.Th w={90}>Durum</Table.Th>
                  <Table.Th w={130} ta="right">İşlemler</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {list.map((c) => (
                  <Table.Tr
                    key={c.id}
                    draggable
                    onDragStart={() => setDragId(c.id)}
                    onDragOver={(e) => dragId && e.preventDefault()}
                    onDrop={() => handleDrop(c.id)}
                    onDragEnd={() => setDragId(null)}
                    style={{
                      cursor: "pointer",
                      background: dragId === c.id ? "var(--mantine-color-blue-0)" : undefined,
                    }}
                    onClick={() => setDetail(c)}
                  >
                    {/* Sürükle tutamacı — satırı tutup sıralamak için */}
                    <Table.Td
                      onClick={(e) => e.stopPropagation()}
                      style={{ cursor: "grab", color: "var(--mantine-color-gray-5)" }}
                      title="Sürükleyerek sırala"
                    >
                      <IconGripVertical size={16} />
                    </Table.Td>
                    <Table.Td><Badge variant="light">{c.code}</Badge></Table.Td>
                    <Table.Td fw={500}>{c.name}</Table.Td>
                    <Table.Td>
                      {c.is_active
                        ? <Badge color="green">Aktif</Badge>
                        : <Badge color="gray">Pasif</Badge>}
                    </Table.Td>
                    <Table.Td onClick={(e) => e.stopPropagation()}>
                      <Group gap={2} wrap="nowrap" justify="flex-end">
                        <ActionIcon variant="subtle" title="Kalem & Tedarikçi yönet" onClick={() => setDetail(c)}>
                          <IconSettings size={16} />
                        </ActionIcon>
                        <ActionIcon variant="subtle" title="Düzenle" onClick={() => startEdit(c)}>
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle" color="red" title="Sil (pasifleştir)"
                          onClick={() => handleDelete(c)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}

      <ResourceForm
        opened={opened}
        onClose={close}
        resource="procurement-categories"
        title={editing ? "Kategori Düzenle" : "Yeni Tedarik Kategorisi"}
        fields={FIELDS}
        editId={editing?.id || null}
        initialValues={editing || undefined}
        transform={(v) => ({ ...v, sort_order: Number(v.sort_order) || 0 })}
      />

      <CategoryDetailDrawer
        category={detail}
        onClose={() => setDetail(null)}
      />
    </Stack>
  );
}

// ============================================================
// KATEGORİ DETAY — kalemler + tedarikçiler TEK EKRANDA yönet.
// "Restoran sahibi kategoriyle beraber hangi toptancılardan tedarik edileceğini
//  kolayca girer" — ana tema.
// ============================================================
function CategoryDetailDrawer({
  category, onClose,
}: {
  category: any | null;
  onClose: () => void;
}) {
  const opened = !!category;
  const [tab, setTab] = useState<"items" | "suppliers">("items");

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      title={
        category ? (
          <Group gap="sm">
            <ThemeIcon size={38} radius="md" variant="light" color="grape">
              <IconTags size={22} />
            </ThemeIcon>
            <div>
              <Text fw={700} size="lg" lh={1.1}>{category.name}</Text>
              <Badge size="xs" variant="light" color="grape">{category.code}</Badge>
            </div>
          </Group>
        ) : ""
      }
    >
      {category && (
        <Stack gap="md">
          <SegmentedControl
            value={tab}
            onChange={(v) => setTab(v as "items" | "suppliers")}
            fullWidth
            data={[
              { label: "📦 Kalemler", value: "items" },
              { label: "🚚 Tedarikçiler", value: "suppliers" },
            ]}
          />
          {tab === "items"
            ? <CategoryItemsManager category={category} />
            : <CategorySuppliersManager category={category} />}
        </Stack>
      )}
    </Drawer>
  );
}

// --- Kalemler sekmesi: bu kategorinin Ingredient + SupplyItem'ları ---
function CategoryItemsManager({ category }: { category: any }) {
  const updateIngredient = useUpdate("ingredients");
  const updateSupplyItem = useUpdate("supply-items");
  const createIngredient = useCreate("ingredients");
  const createSupplyItem = useCreate("supply-items");
  // Tip segmenti: hem yeni kalemin tipini hem listenin filtresini belirler.
  const [addType, setAddType] = useState<"INGREDIENT" | "SUPPLY_ITEM">("INGREDIENT");
  const [newName, setNewName] = useState("");
  const [newBrand, setNewBrand] = useState("");
  const [newSku, setNewSku] = useState("");
  const [newUnit, setNewUnit] = useState("kg");
  const [search, setSearch] = useState("");
  const [debounced] = useDebouncedValue(search.trim(), 300);

  // Server-side aranabilir + sayfalı liste — 100/1000 kalemde bile DOM'a yalnız
  // eşleşen sayfa basılır. Aktif tipe göre ingredients VEYA supply-items.
  const resource = addType === "INGREDIENT" ? "ingredients" : "supply-items";
  const {
    items, total, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage,
  } = useInfiniteResource<any>(resource, {
    search: debounced,
    filters: { category: category.id },
  });
  const sentinelRef = useInfiniteScrollRef<HTMLDivElement>({
    hasNextPage, isFetchingNextPage, fetchNextPage, itemCount: items.length,
  });
  const qc = useQueryClient();

  // Kategori açılınca: hangi tipte kalem varsa o segmenti otomatik seç.
  // (Temizlik'te sadece mutfak-dışı var → "Hammadde"de boş görünmesin.)
  const detectItems = useCategoryItems();
  useEffect(() => {
    detectItems.mutateAsync(category.id).then((res) => {
      if (res.ingredients.length === 0 && res.supply_items.length > 0) {
        setAddType("SUPPLY_ITEM");
      } else if (res.supply_items.length === 0 && res.ingredients.length > 0) {
        setAddType("INGREDIENT");
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category.id]);
  const refresh = () => qc.invalidateQueries({ queryKey: [resource, "infinite"] });

  // Sıfırdan yeni kalem yarat + direkt bu kategoriye bağla. addType tipini belirler.
  async function handleCreate() {
    const name = newName.trim();
    if (!name) return;
    const brand = newBrand.trim();
    // SKU boş bırakılırsa backend kategori koduna göre otomatik üretir (KSP-0001).
    const sku = newSku.trim();
    try {
      if (addType === "INGREDIENT") {
        await createIngredient.mutateAsync({ name, brand, sku, unit: newUnit, category: category.id });
      } else {
        await createSupplyItem.mutateAsync({ name, brand, sku, unit: newUnit, category: category.id });
      }
      notifications.show({ message: `${name} oluşturuldu → ${category.name}`, color: "green" });
      setNewName(""); setNewBrand(""); setNewSku("");
      refresh();
    } catch (e: any) {
      notifications.show({
        message:
          e?.response?.data?.detail ||
          e?.response?.data?.name?.[0] ||
          e?.response?.data?.sku?.[0] ||
          "Hata",
        color: "red",
      });
    }
  }

  async function detach(id: string, name: string) {
    if (addType === "INGREDIENT") {
      await updateIngredient.mutateAsync({ id, category: null });
    } else {
      await updateSupplyItem.mutateAsync({ id, category: null });
    }
    notifications.show({ message: `${name} kategoriden çıkarıldı`, color: "blue" });
    refresh();
  }

  // Kalemin ad/marka/birim'ini düzenle (inline kart formundan).
  async function saveItem(id: string, patch: { name: string; brand: string; unit: string }) {
    const mut = addType === "INGREDIENT" ? updateIngredient : updateSupplyItem;
    await mut.mutateAsync({ id, ...patch });
    notifications.show({ message: "Güncellendi", color: "green" });
    refresh();
  }

  const isIng = addType === "INGREDIENT";

  return (
    <Stack gap="sm">
      {/* Kategoriye kalem ekle = o kategoriden satın alınacak ürünü TANIMLA. */}
      <Paper withBorder radius="md" p="sm" bg="var(--mantine-color-gray-0)">
        <Group justify="space-between" align="flex-end" mb="sm">
          <div>
            <Text size="sm" fw={600}>Yeni Kalem Ekle</Text>
            <Text size="xs" c="dimmed">Bu kategoriden alacağınız ürünü tanımlayın</Text>
          </div>
          <SegmentedControl
            size="xs"
            value={addType}
            onChange={(v) => { setAddType(v as "INGREDIENT" | "SUPPLY_ITEM"); setSearch(""); }}
            data={[
              { label: "Hammadde", value: "INGREDIENT" },
              { label: "Mutfak-dışı", value: "SUPPLY_ITEM" },
            ]}
          />
        </Group>
        <Group gap="xs" align="flex-end">
          <TextInput
            size="xs" style={{ flex: 2 }}
            label={isIng ? "Hammadde adı" : "Kalem adı"}
            placeholder={isIng ? "ör. Kuzu But, Dana Kıyma" : "ör. Çamaşır Suyu 5 LT"}
            value={newName}
            onChange={(e) => setNewName(e.currentTarget.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleCreate(); }}
          />
          <TextInput
            size="xs" style={{ flex: 1 }}
            label="Marka"
            placeholder="ör. Domestos"
            value={newBrand}
            onChange={(e) => setNewBrand(e.currentTarget.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleCreate(); }}
          />
          <TextInput
            size="xs" w={120}
            label="SKU"
            placeholder="boş = otomatik"
            value={newSku}
            onChange={(e) => setNewSku(e.currentTarget.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleCreate(); }}
          />
          <Select
            size="xs" w={84} label="Birim"
            value={newUnit}
            onChange={(v) => setNewUnit(v || "kg")}
            data={["kg", "gram", "adet", "lt", "ml", "paket", "koli"]}
            allowDeselect={false}
            comboboxProps={{ withinPortal: true }}
          />
          <Button
            size="xs" leftSection={<IconPlus size={14} />}
            onClick={handleCreate}
            loading={createIngredient.isPending || createSupplyItem.isPending}
            disabled={!newName.trim()}
          >
            Ekle
          </Button>
        </Group>
      </Paper>

      {/* Liste — aktif tipin kalemleri, aranabilir + sayfalı */}
      <TextInput
        size="xs"
        placeholder={isIng ? "Hammadde ara..." : "Mutfak-dışı kalem ara..."}
        leftSection={<IconSearch size={14} />}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
      />
      <Text size="xs" fw={600} c="dimmed">
        {total} {isIng ? "hammadde" : "mutfak-dışı kalem"}
      </Text>

      {/* Düz akış — liste kısaysa drawer doğal yükseklikte kalır (scroll yok),
          uzunsa drawer'ın kendi scroll'u devreye girer. İçeriğe gereksiz iç
          scroll kutusu dayatmıyoruz. */}
      <div>
        {isLoading ? (
          <Group justify="center" py="xl"><Loader size="sm" /></Group>
        ) : items.length === 0 ? (
          <Stack align="center" py="xl" gap={6}>
            <ThemeIcon size={56} radius="xl" variant="light" color="gray">
              <IconShoppingCart size={28} />
            </ThemeIcon>
            <Text c="dimmed" size="sm" ta="center" maw={280}>
              {debounced
                ? "Eşleşen kalem yok"
                : `Bu kategoride ${isIng ? "hammadde" : "mutfak-dışı kalem"} yok. Yukarıdan ekleyin.`}
            </Text>
          </Stack>
        ) : (
          <Stack gap={6}>
            {items.map((it: any) => (
              <ItemCard
                key={it.id}
                item={it}
                isIngredient={isIng}
                onSave={(patch) => saveItem(it.id, patch)}
                onRemove={() => detach(it.id, it.name)}
              />
            ))}
            {hasNextPage && (
              <Box ref={sentinelRef}>
                <Group justify="center" py="xs"><Loader size="xs" /></Group>
              </Box>
            )}
          </Stack>
        )}
      </div>
    </Stack>
  );
}

// --- Tek kalem kartı: görüntü + inline düzenleme (ad / marka / birim) ---
function ItemCard({
  item, isIngredient, onSave, onRemove,
}: {
  item: any;
  isIngredient: boolean;
  onSave: (patch: { name: string; brand: string; unit: string }) => Promise<void>;
  onRemove: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(item.name);
  const [brand, setBrand] = useState(item.brand || "");
  const [unit, setUnit] = useState(item.unit || "adet");
  const [saving, setSaving] = useState(false);

  function startEdit() {
    setName(item.name); setBrand(item.brand || ""); setUnit(item.unit || "adet");
    setEditing(true);
  }
  async function save() {
    if (!name.trim()) return;
    setSaving(true);
    try {
      await onSave({ name: name.trim(), brand: brand.trim(), unit });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  if (editing) {
    return (
      <Card withBorder radius="md" padding="xs" bg="var(--mantine-color-blue-0)">
        <Group gap="xs" align="flex-end">
          <TextInput
            size="xs" style={{ flex: 2 }} label="Ad"
            value={name} onChange={(e) => setName(e.currentTarget.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); }}
            autoFocus
          />
          <TextInput
            size="xs" style={{ flex: 1 }} label="Marka"
            placeholder="ör. Domestos"
            value={brand} onChange={(e) => setBrand(e.currentTarget.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); }}
          />
          {/* SKU kalemin kalıcı kimliği — oluşturulduktan sonra değiştirilemez. */}
          <TextInput
            size="xs" w={110} label="SKU"
            value={item.sku || "—"}
            readOnly disabled
            title="SKU değiştirilemez (kalıcı stok kodu)"
          />
          <Select
            size="xs" w={84} label="Birim"
            value={unit} onChange={(v) => setUnit(v || "adet")}
            data={["kg", "gram", "adet", "lt", "ml", "paket", "koli"]}
            allowDeselect={false} comboboxProps={{ withinPortal: true }}
          />
          <ActionIcon variant="filled" color="green" radius="md" title="Kaydet"
            onClick={save} loading={saving} disabled={!name.trim()}>
            <IconCheck size={16} />
          </ActionIcon>
          <ActionIcon variant="subtle" radius="md" title="Vazgeç"
            onClick={() => setEditing(false)}>
            <IconX size={16} />
          </ActionIcon>
        </Group>
      </Card>
    );
  }

  return (
    <Card withBorder radius="md" padding="xs">
      <Group justify="space-between" wrap="nowrap">
        <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
          <ThemeIcon size={34} radius="md" variant="light" color={isIngredient ? "blue" : "teal"}>
            {isIngredient ? <IconCheese size={18} /> : <IconSparkles size={18} />}
          </ThemeIcon>
          <div style={{ minWidth: 0 }}>
            <Group gap={6} wrap="nowrap">
              <Text size="sm" fw={600} truncate>{item.name}</Text>
              {item.sku && (
                <Badge size="xs" variant="light">{item.sku}</Badge>
              )}
              {item.brand && (
                <Badge size="xs" variant="outline" color="grape">{item.brand}</Badge>
              )}
            </Group>
            <Group gap={6}>
              <Badge size="xs" variant="light" color={isIngredient ? "blue" : "teal"}>
                {isIngredient ? "Hammadde" : "Mutfak-dışı"}
              </Badge>
              <Text size="xs" c="dimmed">{item.unit}</Text>
            </Group>
          </div>
        </Group>
        <Group gap={2} wrap="nowrap">
          <ActionIcon variant="subtle" radius="md" title="Düzenle" onClick={startEdit}>
            <IconEdit size={16} />
          </ActionIcon>
          <ActionIcon variant="subtle" color="red" radius="md" title="Kategoriden çıkar"
            onClick={onRemove}>
            <IconX size={16} />
          </ActionIcon>
        </Group>
      </Group>
    </Card>
  );
}

// --- Tedarikçiler sekmesi: bu kategoriden mal sağlayan tedarikçiler (M2M) ---
function CategorySuppliersManager({ category }: { category: any }) {
  const { data, refetch, isLoading } = useResource<any>("suppliers", {
    filters: { categories: category.id },
  });
  const updateSupplier = useUpdate("suppliers");
  const [pickerOpen, setPickerOpen] = useState(false);
  const suppliers: any[] = data?.results || data || [];

  async function attach(s: any) {
    // KRİTİK: picker dar Supplier döndürür ama API categories'i de taşır.
    // Mevcut kategorileri KORU (M2M replace siler) — yoksa diğer kategoriler kaybolur.
    const existing: string[] = s.categories || [];
    const next = Array.from(new Set([...existing, category.id]));
    await updateSupplier.mutateAsync({ id: s.id, categories: next });
    notifications.show({ message: `${s.name} → ${category.name}`, color: "green" });
    refetch();
  }
  async function detach(s: any) {
    const next = (s.categories || []).filter((c: string) => c !== category.id);
    await updateSupplier.mutateAsync({ id: s.id, categories: next });
    notifications.show({ message: `${s.name} çıkarıldı`, color: "blue" });
    refetch();
  }

  return (
    <Stack gap="sm">
      <Paper withBorder radius="md" p="sm" bg="var(--mantine-color-gray-0)">
        <Group justify="space-between" align="center">
          <div>
            <Text size="sm" fw={600}>Tedarikçi Ekle</Text>
            <Text size="xs" c="dimmed">
              RFQ'da "{category.name}" kalemleri için otomatik önerilir
            </Text>
          </div>
          <Button
            size="xs" leftSection={<IconPlus size={14} />}
            onClick={() => setPickerOpen(true)}
          >
            Ekle
          </Button>
        </Group>
      </Paper>

      <Text size="sm" fw={600} c="dimmed">{suppliers.length} tedarikçi</Text>

      {isLoading
        ? <Group justify="center" py="xl"><Loader size="sm" /></Group>
        : suppliers.length === 0
          ? (
            <Stack align="center" py="xl" gap={6}>
              <ThemeIcon size={56} radius="xl" variant="light" color="gray">
                <IconTruck size={28} />
              </ThemeIcon>
              <Text c="dimmed" size="sm" ta="center" maw={280}>
                Bu kategoriden mal sağlayan toptancı yok. "Ekle" ile bağlayın —
                RFQ önerilerinde otomatik çıkarlar.
              </Text>
            </Stack>
          )
          : (
            <Stack gap={6}>
              {suppliers.map((s) => (
                <Card key={s.id} withBorder radius="md" padding="xs">
                  <Group justify="space-between" wrap="nowrap">
                    <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                      <ThemeIcon size={34} radius="md" variant="light" color="orange">
                        <IconBuildingStore size={18} />
                      </ThemeIcon>
                      <div style={{ minWidth: 0 }}>
                        <Text size="sm" fw={600} truncate>{s.name}</Text>
                        {s.tax_id && <Text size="xs" c="dimmed">VKN: {s.tax_id}</Text>}
                      </div>
                    </Group>
                    <ActionIcon
                      variant="subtle" color="red" radius="md"
                      title="Kategoriden çıkar" onClick={() => detach(s)}
                    >
                      <IconX size={16} />
                    </ActionIcon>
                  </Group>
                </Card>
              ))}
            </Stack>
          )}

      <SupplierPickerModal
        opened={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(s) => attach(s)}
        excludeIds={suppliers.map((s) => s.id)}
      />
    </Stack>
  );
}
